import { useMutation } from "@tanstack/react-query";

import { ApiError, isValidationError } from "@config/apiClient";
import { queryClient } from "@config/queryClient";
import companyService from "@services/company.service";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES, SUCCESS_MESSAGES } from "@config/messages";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { useSwitches } from "@hooks/useSwitches";
import { DisconnectedError } from "@app-types/errors";
import { CreateCompanyRequest } from "@app-types/requests.type";
import { COMPANY_QUERY_KEY, UPDATE_COMPANY_MUTATION_KEY } from "@config/query";

export type UpdateCompanyPayload = Partial<CreateCompanyRequest> & {
  id: string;
  // Shared attributes land on the company and its deals; toast that instead of the plain one.
  hasSharedAttributeUpdates?: boolean;
  // Opts out of backend required-attribute validation, as the deal dialog's document flow does.
  skipRequiredAttributes?: boolean;
};

export function useUpdateCompany(onSuccess?: VoidFunction) {
  const { isAvailable } = useSwitches();
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const hasMultipleActiveDeals = isAvailable("MULTIPLE_ACTIVE_DEALS");

  return useMutation({
    mutationKey: [UPDATE_COMPANY_MUTATION_KEY],
    mutationFn: async (data: UpdateCompanyPayload) => {
      const { id, hasSharedAttributeUpdates, skipRequiredAttributes, ...req } = data;
      return await companyService.updateCompany(id, req, skipRequiredAttributes);
    },
    onSuccess: (response, variables) => {
      onSuccess?.();
      queryClient.setQueryData([COMPANY_QUERY_KEY, response.id], response);

      if (variables.hasSharedAttributeUpdates) {
        const message = hasMultipleActiveDeals
          ? SUCCESS_MESSAGES.SOURCE_COMPANY_WITH_MULTI_ACTIVE_DEALS_UPDATED
          : SUCCESS_MESSAGES.SOURCE_COMPANY_UPDATED;

        ToastService.showUserError(message, { id: "source-company-updated" });
        return;
      }

      ToastService.showSuccess(SUCCESS_MESSAGES.COMPANY_UPDATED(response.title));
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      if (!isValidationError(error)) notifyBugsnag(error, ERROR_STRINGS.UPDATE_COMPANY);
      // A 400 carries the reason the user needs (e.g. a required attribute); the generic
      // message is only for failures with nothing readable to show.
      ToastService.showError(
        error as ApiError,
        isValidationError(error) ? undefined : ERROR_MESSAGES.UPDATE_COMPANY
      );
    },
  });
}
